import { useState, useEffect } from "react";
import { useThemeContext } from "../../../../context/ThemeContext";
import NavigationButtons from "../Shared/NavigationButtons";
import { Info, AlertCircle, CheckCircle } from "lucide-react";
import API from "../../../../utils/API";

const Step5COPOArticulation = ({
  courseOutcomes,
  programOutcomes,
  programSpecificOutcomes,
  onBack,
  onNext,
  darkMode,
  initialData = null,
  onDataChange = () => {},
  selectedCourse,
  academicYear,
}) => {
  const [coPoMatrix, setCoPoMatrix] = useState([]);
  const [coPsoMatrix, setCoPsoMatrix] = useState([]);
  const [indirectScores, setIndirectScores] = useState([]);
  const [activeTab, setActiveTab] = useState("po"); // 'po', 'pso', or 'indirect'
  const [tabsCompleted, setTabsCompleted] = useState({
    po: false,
    pso: false,
    indirect: false,
  });
  const [isLoading, setIsLoading] = useState(false);

  // Initialize the matrices with default values or existing data
  useEffect(() => {
    const initializeData = async () => {
      if (initialData) {
        // Use existing data if available
        setCoPoMatrix(initialData.co_po_matrix || []);
        setCoPsoMatrix(initialData.co_pso_matrix || []);

        // Check if PO and PSO tabs are completed
        setTabsCompleted((prev) => ({
          ...prev,
          po: initialData.co_po_matrix && initialData.co_po_matrix.length > 0,
          pso:
            initialData.co_pso_matrix && initialData.co_pso_matrix.length > 0,
        }));
      } else {
        // Initialize CO-PO matrix
        const initialCoPoMatrix = courseOutcomes.map((co) => ({
          co_id: co.co_id,
          po_mappings: programOutcomes.map((po) => ({
            po_id: po.po_id,
            strength: 0,
          })),
        }));
        setCoPoMatrix(initialCoPoMatrix);

        // Initialize CO-PSO matrix
        const initialCoPsoMatrix = courseOutcomes.map((co) => ({
          co_id: co.co_id,
          pso_mappings: programSpecificOutcomes.map((pso) => ({
            pso_id: pso.pso_id,
            strength: 0,
          })),
        }));
        setCoPsoMatrix(initialCoPsoMatrix);
      }

      // Initialize indirect scores
      const initialIndirectScores = courseOutcomes.map((co) => ({
        co_id: co.co_id,
        indirect_score: 0,
      }));
      setIndirectScores(initialIndirectScores);
    };

    initializeData();
  }, [courseOutcomes, programOutcomes, programSpecificOutcomes, initialData]);

  // Fetch existing indirect scores when component mounts
  useEffect(() => {
    const fetchIndirectScores = async () => {
      if (selectedCourse && academicYear) {
        setIsLoading(true);
        try {
          const response = await API.get("ece/faculty/indirect-scores", {
            params: {
              course_code: selectedCourse.course_code,
              section_name: selectedCourse.section_name,
              academic_year: academicYear,
              semester_type: selectedCourse.semester_type || "even",
            },
          });

          if (response.data && response.data.data) {
            const scores = response.data.data;
            setIndirectScores((prev) =>
              prev.map((item) => {
                const existingScore = scores.find(
                  (score) => score.co_id === item.co_id
                );
                return existingScore
                  ? { ...item, indirect_score: existingScore.indirect_score }
                  : item;
              })
            );
            setTabsCompleted((prev) => ({
              ...prev,
              indirect: scores.length > 0,
            }));
          }
        } catch (error) {
          console.error("Error fetching indirect scores:", error);
          // It's okay if no indirect scores exist yet
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchIndirectScores();
  }, [selectedCourse, academicYear]);

  // Update parent whenever data changes
  useEffect(() => {
    if (
      coPoMatrix.length > 0 &&
      coPsoMatrix.length > 0 &&
      indirectScores.length > 0
    ) {
      onDataChange({
        co_po_matrix: coPoMatrix,
        co_pso_matrix: coPsoMatrix,
        indirect_scores: indirectScores,
      });
    }
  }, [coPoMatrix, coPsoMatrix, indirectScores, onDataChange]);

  // Handle input change for CO-PO matrix
  const handlePoStrengthChange = (coId, poId, value) => {
    if (value === "" || (!isNaN(value) && value >= 0 && value <= 3)) {
      const updatedMatrix = coPoMatrix.map((coMapping) => {
        if (coMapping.co_id === coId) {
          return {
            ...coMapping,
            po_mappings: coMapping.po_mappings.map((poMapping) =>
              poMapping.po_id === poId
                ? { ...poMapping, strength: value === "" ? "" : Number(value) }
                : poMapping
            ),
          };
        }
        return coMapping;
      });
      setCoPoMatrix(updatedMatrix);

      // Mark PO tab as completed if any value is set
      const hasValues = updatedMatrix.some((co) =>
        co.po_mappings.some((po) => po.strength > 0)
      );
      setTabsCompleted((prev) => ({ ...prev, po: hasValues }));
    }
  };

  // Handle input change for CO-PSO matrix
  const handlePsoStrengthChange = (coId, psoId, value) => {
    if (value === "" || (!isNaN(value) && value >= 0 && value <= 3)) {
      const updatedMatrix = coPsoMatrix.map((coMapping) => {
        if (coMapping.co_id === coId) {
          return {
            ...coMapping,
            pso_mappings: coMapping.pso_mappings.map((psoMapping) =>
              psoMapping.pso_id === psoId
                ? { ...psoMapping, strength: value === "" ? "" : Number(value) }
                : psoMapping
            ),
          };
        }
        return coMapping;
      });
      setCoPsoMatrix(updatedMatrix);

      // Mark PSO tab as completed if any value is set
      const hasValues = updatedMatrix.some((co) =>
        co.pso_mappings.some((pso) => pso.strength > 0)
      );
      setTabsCompleted((prev) => ({ ...prev, pso: hasValues }));
    }
  };

  // Handle input change for indirect scores
  const handleIndirectScoreChange = (coId, value) => {
    if (value === "" || (!isNaN(value) && value >= 0 && value <= 3)) {
      const updatedScores = indirectScores.map((score) =>
        score.co_id === coId
          ? { ...score, indirect_score: value === "" ? "" : Number(value) }
          : score
      );
      setIndirectScores(updatedScores);

      // Mark indirect tab as completed if any value is set
      const hasValues = updatedScores.some((score) => score.indirect_score > 0);
      setTabsCompleted((prev) => ({ ...prev, indirect: hasValues }));
    }
  };

  // Handle next button click - navigate between tabs or submit
  const handleNext = () => {
    // Convert empty strings to 0 before processing
    const processedCoPoMatrix = coPoMatrix.map((coMapping) => ({
      ...coMapping,
      po_mappings: coMapping.po_mappings.map((poMapping) => ({
        ...poMapping,
        strength: poMapping.strength === "" ? 0 : Number(poMapping.strength),
      })),
    }));

    const processedCoPsoMatrix = coPsoMatrix.map((coMapping) => ({
      ...coMapping,
      pso_mappings: coMapping.pso_mappings.map((psoMapping) => ({
        ...psoMapping,
        strength: psoMapping.strength === "" ? 0 : Number(psoMapping.strength),
      })),
    }));

    const processedIndirectScores = indirectScores.map((score) => ({
      ...score,
      indirect_score:
        score.indirect_score === "" ? 0 : Number(score.indirect_score),
    }));

    // Navigate to next tab or submit if all tabs are completed
    if (activeTab === "po") {
      setActiveTab("pso");
    } else if (activeTab === "pso") {
      setActiveTab("indirect");
    } else if (activeTab === "indirect") {
      // Submit all data
      onNext({
        co_po_matrix: processedCoPoMatrix,
        co_pso_matrix: processedCoPsoMatrix,
        indirect_scores: processedIndirectScores,
      });
    }
  };

  // Handle back button click - navigate between tabs or go to previous step
  const handleBack = () => {
    if (activeTab === "pso") {
      setActiveTab("po");
    } else if (activeTab === "indirect") {
      setActiveTab("pso");
    } else {
      onBack();
    }
  };

  // Get strength description for tooltip
  const getStrengthDescription = (strength) => {
    switch (strength) {
      case 0:
        return "No correlation";
      case 1:
        return "Low correlation";
      case 2:
        return "Medium correlation";
      case 3:
        return "High correlation";
      default:
        return "Select correlation strength (0-3)";
    }
  };

  // Check if current tab is completed
  const isCurrentTabCompleted = () => {
    if (activeTab === "po") {
      return coPoMatrix.some((co) =>
        co.po_mappings.some((po) => po.strength > 0)
      );
    } else if (activeTab === "pso") {
      return coPsoMatrix.some((co) =>
        co.pso_mappings.some((pso) => pso.strength > 0)
      );
    } else if (activeTab === "indirect") {
      return indirectScores.some((score) => score.indirect_score > 0);
    }
    return false;
  };

  // Get next button text based on current tab
  const getNextButtonText = () => {
    if (activeTab === "po") return "Next: PSO Mapping";
    if (activeTab === "pso") return "Next: Indirect Assessment";
    if (activeTab === "indirect") return "Save & Continue";
    return "Next";
  };

  return (
    <div
      className={`p-6 rounded-xl mb-4 transition-all duration-300 ${
        darkMode
          ? "bg-gray-900 border border-gray-800"
          : "bg-white border border-gray-200 shadow-sm"
      }`}
    >
      <div className="mb-6">
        <h3
          className={`text-xl font-bold mb-2 ${
            darkMode ? "text-white" : "text-gray-900"
          }`}
        >
          CO-PO/PSO Articulation & Indirect Assessment
        </h3>

        <p
          className={`text-sm flex items-start ${darkMode ? "text-gray-400" : "text-gray-600"}`}
        >
          <Info size={16} className="mr-2 mt-0.5 flex-shrink-0" />
          Define the correlation strength between Course Outcomes and Program
          Outcomes/PSOs, and set indirect assessment scores.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex mb-6 border-b">
        <button
          className={`py-2 px-4 font-medium text-sm flex items-center ${
            activeTab === "po"
              ? darkMode
                ? "text-red-400 border-b-2 border-red-400"
                : "text-red-600 border-b-2 border-red-600"
              : darkMode
                ? "text-gray-400 hover:text-gray-300"
                : "text-gray-500 hover:text-gray-700"
          }`}
          onClick={() => setActiveTab("po")}
        >
          CO-PO Matrix
          {tabsCompleted.po && (
            <CheckCircle size={14} className="ml-2 text-green-500" />
          )}
        </button>
        <button
          className={`py-2 px-4 font-medium text-sm flex items-center ${
            activeTab === "pso"
              ? darkMode
                ? "text-red-400 border-b-2 border-red-400"
                : "text-red-600 border-b-2 border-red-600"
              : darkMode
                ? "text-gray-400 hover:text-gray-300"
                : "text-gray-500 hover:text-gray-700"
          }`}
          onClick={() => setActiveTab("pso")}
        >
          CO-PSO Matrix
          {tabsCompleted.pso && (
            <CheckCircle size={14} className="ml-2 text-green-500" />
          )}
        </button>
        <button
          className={`py-2 px-4 font-medium text-sm flex items-center ${
            activeTab === "indirect"
              ? darkMode
                ? "text-red-400 border-b-2 border-red-400"
                : "text-red-600 border-b-2 border-red-600"
              : darkMode
                ? "text-gray-400 hover:text-gray-300"
                : "text-gray-500 hover:text-gray-700"
          }`}
          onClick={() => setActiveTab("indirect")}
        >
          Indirect Assessment
          {tabsCompleted.indirect && (
            <CheckCircle size={14} className="ml-2 text-green-500" />
          )}
        </button>
      </div>

      {/* Correlation Legend */}
      <div
        className="mb-4 p-3 rounded-lg border"
        style={{
          backgroundColor: darkMode ? "rgba(55, 65, 81, 0.3)" : "#F9FAFB",
          borderColor: darkMode ? "#374151" : "#E5E7EB",
        }}
      >
        <p
          className={`text-xs font-medium mb-1 ${darkMode ? "text-gray-300" : "text-gray-700"}`}
        >
          {activeTab === "indirect"
            ? "Indirect Assessment Guide:"
            : "Correlation Strength Guide:"}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          {activeTab === "indirect" ? (
            <>
              <div
                className={`flex items-center ${darkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                <div className="w-3 h-3 rounded-sm bg-gray-400 mr-2"></div>
                <span>0 - Not attained</span>
              </div>
              <div
                className={`flex items-center ${darkMode ? "text-blue-400" : "text-blue-600"}`}
              >
                <div className="w-3 h-3 rounded-sm bg-blue-400 mr-2"></div>
                <span>1 - Partially attained</span>
              </div>
              <div
                className={`flex items-center ${darkMode ? "text-green-400" : "text-green-600"}`}
              >
                <div className="w-3 h-3 rounded-sm bg-green-400 mr-2"></div>
                <span>2 - Mostly attained</span>
              </div>
              <div
                className={`flex items-center ${darkMode ? "text-red-400" : "text-red-600"}`}
              >
                <div className="w-3 h-3 rounded-sm bg-red-400 mr-2"></div>
                <span>3 - Fully attained</span>
              </div>
            </>
          ) : (
            <>
              <div
                className={`flex items-center ${darkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                <div className="w-3 h-3 rounded-sm bg-gray-400 mr-2"></div>
                <span>0 - None</span>
              </div>
              <div
                className={`flex items-center ${darkMode ? "text-blue-400" : "text-blue-600"}`}
              >
                <div className="w-3 h-3 rounded-sm bg-blue-400 mr-2"></div>
                <span>1 - Low</span>
              </div>
              <div
                className={`flex items-center ${darkMode ? "text-green-400" : "text-green-600"}`}
              >
                <div className="w-3 h-3 rounded-sm bg-green-400 mr-2"></div>
                <span>2 - Medium</span>
              </div>
              <div
                className={`flex items-center ${darkMode ? "text-red-400" : "text-red-600"}`}
              >
                <div className="w-3 h-3 rounded-sm bg-red-400 mr-2"></div>
                <span>3 - High</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          <span className="ml-3 text-gray-500">Loading data...</span>
        </div>
      )}

      {/* CO-PO Matrix */}
      {activeTab === "po" && !isLoading && (
        <div
          className="overflow-x-auto rounded-lg border"
          style={{
            borderColor: darkMode ? "#374151" : "#E5E7EB",
          }}
        >
          <table className="w-full">
            <thead>
              <tr
                style={{
                  backgroundColor: darkMode ? "#1F2937" : "#F9FAFB",
                }}
              >
                <th
                  className="p-3 text-left text-sm font-semibold"
                  style={{
                    color: darkMode ? "#D1D5DB" : "#374151",
                    borderBottom: darkMode
                      ? "1px solid #374151"
                      : "1px solid #E5E7EB",
                  }}
                >
                  Course Outcomes
                </th>
                {programOutcomes.map((po) => (
                  <th
                    key={po.po_id}
                    className="p-3 text-center text-sm font-semibold"
                    style={{
                      color: darkMode ? "#D1D5DB" : "#374151",
                      borderBottom: darkMode
                        ? "1px solid #374151"
                        : "1px solid #E5E7EB",
                    }}
                  >
                    PO{po.po_number}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {courseOutcomes.map((co, coIndex) => {
                const coMapping = coPoMatrix.find((m) => m.co_id === co.co_id);

                return (
                  <tr
                    key={co.co_id}
                    style={{
                      backgroundColor:
                        coIndex % 2 === 0
                          ? darkMode
                            ? "rgba(31, 41, 55, 0.5)"
                            : "#FFFFFF"
                          : darkMode
                            ? "rgba(31, 41, 55, 0.3)"
                            : "#F9FAFB",
                      borderBottom: darkMode
                        ? "1px solid #374151"
                        : "1px solid #F3F4F6",
                    }}
                  >
                    <td
                      className="p-3 font-medium text-sm"
                      style={{
                        color: darkMode ? "#E5E7EB" : "#374151",
                      }}
                    >
                      CO{co.co_number}
                    </td>
                    {programOutcomes.map((po) => {
                      const poMapping = coMapping?.po_mappings.find(
                        (m) => m.po_id === po.po_id
                      );
                      const strength = poMapping?.strength ?? 0;

                      return (
                        <td
                          key={`${co.co_id}-${po.po_id}`}
                          className="p-3 text-center"
                        >
                          <input
                            type="number"
                            min="0"
                            max="3"
                            value={strength}
                            onChange={(e) =>
                              handlePoStrengthChange(
                                co.co_id,
                                po.po_id,
                                e.target.value
                              )
                            }
                            className={`w-12 p-2 rounded border text-sm text-center transition-colors ${
                              darkMode
                                ? "bg-gray-800 border-gray-700 text-white focus:border-red-500 focus:ring-2 focus:ring-red-500/30"
                                : "bg-white border-gray-300 text-gray-900 focus:border-red-500 focus:ring-2 focus:ring-red-500/20"
                            } ${
                              strength === 0
                                ? "bg-opacity-50"
                                : strength === 1
                                  ? "bg-blue-100 border-blue-200"
                                  : strength === 2
                                    ? "bg-green-100 border-green-200"
                                    : strength === 3
                                      ? "bg-red-100 border-red-200"
                                      : ""
                            }`}
                            title={getStrengthDescription(strength)}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* CO-PSO Matrix */}
      {activeTab === "pso" && !isLoading && (
        <div
          className="overflow-x-auto rounded-lg border"
          style={{
            borderColor: darkMode ? "#374151" : "#E5E7EB",
          }}
        >
          <table className="w-full">
            <thead>
              <tr
                style={{
                  backgroundColor: darkMode ? "#1F2937" : "#F9FAFB",
                }}
              >
                <th
                  className="p-3 text-left text-sm font-semibold"
                  style={{
                    color: darkMode ? "#D1D5DB" : "#374151",
                    borderBottom: darkMode
                      ? "1px solid #374151"
                      : "1px solid #E5E7EB",
                  }}
                >
                  Course Outcomes
                </th>
                {programSpecificOutcomes.map((pso) => (
                  <th
                    key={pso.pso_id}
                    className="p-3 text-center text-sm font-semibold"
                    style={{
                      color: darkMode ? "#D1D5DB" : "#374151",
                      borderBottom: darkMode
                        ? "1px solid #374151"
                        : "1px solid #E5E7EB",
                    }}
                  >
                    PSO{pso.pso_number}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {courseOutcomes.map((co, coIndex) => {
                const coMapping = coPsoMatrix.find((m) => m.co_id === co.co_id);

                return (
                  <tr
                    key={co.co_id}
                    style={{
                      backgroundColor:
                        coIndex % 2 === 0
                          ? darkMode
                            ? "rgba(31, 41, 55, 0.5)"
                            : "#FFFFFF"
                          : darkMode
                            ? "rgba(31, 41, 55, 0.3)"
                            : "#F9FAFB",
                      borderBottom: darkMode
                        ? "1px solid #374151"
                        : "1px solid #F3F4F6",
                    }}
                  >
                    <td
                      className="p-3 font-medium text-sm"
                      style={{
                        color: darkMode ? "#E5E7EB" : "#374151",
                      }}
                    >
                      CO{co.co_number}
                    </td>
                    {programSpecificOutcomes.map((pso) => {
                      const psoMapping = coMapping?.pso_mappings.find(
                        (m) => m.pso_id === pso.pso_id
                      );
                      const strength = psoMapping?.strength ?? 0;

                      return (
                        <td
                          key={`${co.co_id}-${pso.pso_id}`}
                          className="p-3 text-center"
                        >
                          <input
                            type="number"
                            min="0"
                            max="3"
                            value={strength}
                            onChange={(e) =>
                              handlePsoStrengthChange(
                                co.co_id,
                                pso.pso_id,
                                e.target.value
                              )
                            }
                            className={`w-12 p-2 rounded border text-sm text-center transition-colors ${
                              darkMode
                                ? "bg-gray-800 border-gray-700 text-white focus:border-red-500 focus:ring-2 focus:ring-red-500/30"
                                : "bg-white border-gray-300 text-gray-900 focus:border-red-500 focus:ring-2 focus:ring-red-500/20"
                            } ${
                              strength === 0
                                ? "bg-opacity-50"
                                : strength === 1
                                  ? "bg-blue-100 border-blue-200"
                                  : strength === 2
                                    ? "bg-green-100 border-green-200"
                                    : strength === 3
                                      ? "bg-red-100 border-red-200"
                                      : ""
                            }`}
                            title={getStrengthDescription(strength)}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Indirect Assessment */}
      {activeTab === "indirect" && !isLoading && (
        <div
          className="overflow-x-auto rounded-lg border"
          style={{
            borderColor: darkMode ? "#374151" : "#E5E7EB",
          }}
        >
          <table className="w-full">
            <thead>
              <tr
                style={{
                  backgroundColor: darkMode ? "#1F2937" : "#F9FAFB",
                }}
              >
                <th
                  className="p-3 text-left text-sm font-semibold"
                  style={{
                    color: darkMode ? "#D1D5DB" : "#374151",
                    borderBottom: darkMode
                      ? "1px solid #374151"
                      : "1px solid #E5E7EB",
                  }}
                >
                  Course Outcome
                </th>
                <th
                  className="p-3 text-left text-sm font-semibold"
                  style={{
                    color: darkMode ? "#D1D5DB" : "#374151",
                    borderBottom: darkMode
                      ? "1px solid #374151"
                      : "1px solid #E5E7EB",
                  }}
                >
                  Description
                </th>
                <th
                  className="p-3 text-center text-sm font-semibold"
                  style={{
                    color: darkMode ? "#D1D5DB" : "#374151",
                    borderBottom: darkMode
                      ? "1px solid #374151"
                      : "1px solid #E5E7EB",
                  }}
                >
                  Indirect Score (0-3)
                </th>
              </tr>
            </thead>
            <tbody>
              {courseOutcomes.map((co, coIndex) => {
                const scoreData = indirectScores.find(
                  (s) => s.co_id === co.co_id
                );
                const score = scoreData?.indirect_score ?? 0;

                return (
                  <tr
                    key={co.co_id}
                    style={{
                      backgroundColor:
                        coIndex % 2 === 0
                          ? darkMode
                            ? "rgba(31, 41, 55, 0.5)"
                            : "#FFFFFF"
                          : darkMode
                            ? "rgba(31, 41, 55, 0.3)"
                            : "#F9FAFB",
                      borderBottom: darkMode
                        ? "1px solid #374151"
                        : "1px solid #F3F4F6",
                    }}
                  >
                    <td
                      className="p-3 font-medium text-sm"
                      style={{
                        color: darkMode ? "#E5E7EB" : "#374151",
                      }}
                    >
                      CO{co.co_number}
                    </td>
                    <td
                      className="p-3 text-sm"
                      style={{
                        color: darkMode ? "#D1D5DB" : "#374151",
                      }}
                    >
                      {co.co_description}
                    </td>
                    <td className="p-3 text-center">
                      <input
                        type="number"
                        min="0"
                        max="3"
                        step="0.1"
                        value={score}
                        onChange={(e) =>
                          handleIndirectScoreChange(co.co_id, e.target.value)
                        }
                        className={`w-16 p-2 rounded border text-sm text-center transition-colors ${
                          darkMode
                            ? "bg-gray-800 border-gray-700 text-white focus:border-red-500 focus:ring-2 focus:ring-red-500/30"
                            : "bg-white border-gray-300 text-gray-900 focus:border-red-500 focus:ring-2 focus:ring-red-500/20"
                        } ${
                          score === 0
                            ? "bg-opacity-50"
                            : score <= 1
                              ? "bg-blue-100 border-blue-200"
                              : score <= 2
                                ? "bg-green-100 border-green-200"
                                : "bg-red-100 border-red-200"
                        }`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex flex-col-reverse md:flex-row justify-between items-center pt-6 gap-3">
        <button
          className={`px-5 py-2.5 rounded-lg font-medium transition-colors w-full md:w-auto text-sm ${
            darkMode
              ? "bg-gray-800 text-white hover:bg-gray-700 border border-gray-700"
              : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-300"
          }`}
          onClick={handleBack}
        >
          {activeTab === "po" ? "Back to Assessment Matrix" : "Back"}
        </button>

        <button
          className={`px-5 py-2.5 rounded-lg font-medium flex items-center justify-center transition-colors w-full md:w-auto text-sm ${
            darkMode
              ? "bg-red-700 hover:bg-red-600 text-white"
              : "bg-red-600 hover:bg-red-700 text-white"
          }`}
          onClick={handleNext}
        >
          {getNextButtonText()}
        </button>
      </div>

      {/* Tab completion status */}
      <div className="mt-4 flex flex-wrap gap-2">
        {!tabsCompleted.po && activeTab !== "po" && (
          <div
            className={`text-xs px-2 py-1 rounded-full flex items-center ${
              darkMode
                ? "bg-yellow-900/30 text-yellow-300"
                : "bg-yellow-100 text-yellow-800"
            }`}
          >
            <AlertCircle size={12} className="mr-1" />
            CO-PO mapping incomplete
          </div>
        )}
        {!tabsCompleted.pso && activeTab !== "pso" && (
          <div
            className={`text-xs px-2 py-1 rounded-full flex items-center ${
              darkMode
                ? "bg-yellow-900/30 text-yellow-300"
                : "bg-yellow-100 text-yellow-800"
            }`}
          >
            <AlertCircle size={12} className="mr-1" />
            CO-PSO mapping incomplete
          </div>
        )}
        {!tabsCompleted.indirect && activeTab !== "indirect" && (
          <div
            className={`text-xs px-2 py-1 rounded-full flex items-center ${
              darkMode
                ? "bg-yellow-900/30 text-yellow-300"
                : "bg-yellow-100 text-yellow-800"
            }`}
          >
            <AlertCircle size={12} className="mr-1" />
            Indirect assessment incomplete
          </div>
        )}
      </div>
    </div>
  );
};

export default Step5COPOArticulation;
